import { useState } from "react";
import { Modal, Form, Input, Select, DatePicker, InputNumber, notification } from "antd";
import { useForm } from "antd/es/form/Form";
import APIClient from "../api/api";
import { CreateManualSalesActivityRequest, ListingActivityType } from "../api/models";
import dayjs from "dayjs";

export interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const KNOWN_VENUES = [
    { value: 'afternic', label: 'Afternic' },
    { value: 'dan', label: 'Dan' },
    { value: 'sedo', label: 'Sedo' },
    { value: 'atom', label: 'Atom' },
];

const ACTIVITY_TYPES = [
    { value: 'sold', label: 'Sold' },
    { value: 'payout', label: 'Payout' },
    { value: 'transferComplete', label: 'Transfer Complete' },
    { value: 'transferRequest', label: 'Transfer Request' },
    { value: 'cancelled', label: 'Cancelled' },
];

const TRANSFER_TYPES = [
    { value: 'regular', label: 'Regular' },
    { value: 'fast', label: 'Fast' },
];

const PAYOUT_TYPES = [
    { value: 'full', label: 'Full' },
    { value: 'installment', label: 'Installment (LTO)' },
];

const CURRENCIES = [
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' },
    { value: 'GBP', label: 'GBP' },
];

const AddSalesRecordModal = (props: Props) => {
    const [form] = useForm();
    const [loading, setLoading] = useState(false);
    const [activityType, setActivityType] = useState<string>('');
    const [payoutType, setPayoutType] = useState<string>('full');
    const [venueMode, setVenueMode] = useState<'select' | 'custom'>('select');

    const handleActivityTypeChange = (value: string) => {
        setActivityType(value);
        // Reset conditional fields when type changes
        form.setFieldsValue({
            salePrice: undefined,
            transferType: 'regular',
            payoutType: 'full',
            installments: undefined,
            amount: undefined,
            installmentNumber: undefined,
            totalInstallments: undefined,
            remainingBalance: undefined,
            currency: 'USD',
        });
        setPayoutType('full');
    };

    const handlePayoutTypeChange = (value: string) => {
        setPayoutType(value);
    };

    async function handleSubmit() {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const venue = venueMode === 'custom' ? values.customVenue : values.venue;

            const req: CreateManualSalesActivityRequest = {
                domain: values.domain.trim().toLowerCase(),
                activityType: values.activityType as ListingActivityType,
                venue: venue,
                account: values.account.trim(),
                createdAt: values.createdAt.toISOString(),
            };

            // Add type-specific fields
            if (values.activityType === 'sold') {
                req.salePrice = values.salePrice;
                req.transferType = values.transferType || 'regular';
                req.payoutType = values.payoutType || 'full';
                if (values.payoutType === 'installment' && values.installments) {
                    req.installments = values.installments;
                }
            } else if (values.activityType === 'payout') {
                req.amount = values.amount;
                req.payoutType = values.payoutType || 'full';
                req.currency = values.currency || 'USD';
                if (values.payoutType === 'installment') {
                    req.installmentNumber = values.installmentNumber;
                    req.totalInstallments = values.totalInstallments;
                    req.remainingBalance = values.remainingBalance;
                }
            } else if (values.activityType === 'transferComplete' || values.activityType === 'transferRequest') {
                req.transferType = values.transferType || 'regular';
            }

            const resp = await APIClient.createManualSalesActivity(req);

            if (resp.success) {
                notification.success({ message: 'Sales record created successfully' });
                form.resetFields();
                setActivityType('');
                setPayoutType('full');
                setVenueMode('select');
                props.onSuccess();
                props.onClose();
            } else {
                notification.error({ message: resp.message || 'Failed to create sales record' });
            }
        } catch (e: any) {
            console.error(e);
            if (e.response?.data?.message) {
                notification.error({ message: e.response.data.message });
            } else {
                notification.error({ message: 'Failed to create sales record' });
            }
        } finally {
            setLoading(false);
        }
    }

    function handleCancel() {
        form.resetFields();
        setActivityType('');
        setPayoutType('full');
        setVenueMode('select');
        props.onClose();
    }

    const showSoldFields = activityType === 'sold';
    const showPayoutFields = activityType === 'payout';
    const showTransferFields = activityType === 'transferComplete' || activityType === 'transferRequest';
    const showInstallmentFields = (showSoldFields || showPayoutFields) && payoutType === 'installment';

    return (
        <Modal
            title="Add Sales Record"
            open={props.open}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={loading}
            okText="Create"
            width={600}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    transferType: 'regular',
                    payoutType: 'full',
                    currency: 'USD',
                    createdAt: dayjs(),
                }}
            >
                {/* Common Fields */}
                <Form.Item
                    name="domain"
                    label="Domain"
                    rules={[
                        { required: true, message: 'Domain is required' },
                        { pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}$/, message: 'Enter a valid domain name' }
                    ]}
                >
                    <Input placeholder="example.com" />
                </Form.Item>

                <Form.Item
                    name="activityType"
                    label="Activity Type"
                    rules={[{ required: true, message: 'Activity type is required' }]}
                >
                    <Select
                        options={ACTIVITY_TYPES}
                        onChange={handleActivityTypeChange}
                        placeholder="Select activity type"
                    />
                </Form.Item>

                <Form.Item label="Venue" required>
                    <Input.Group compact>
                        <Select
                            style={{ width: '30%' }}
                            value={venueMode}
                            onChange={(v) => {
                                setVenueMode(v);
                                form.setFieldsValue({ venue: undefined, customVenue: undefined });
                            }}
                        >
                            <Select.Option value="select">Select</Select.Option>
                            <Select.Option value="custom">Custom</Select.Option>
                        </Select>
                        {venueMode === 'select' ? (
                            <Form.Item
                                name="venue"
                                noStyle
                                rules={[{ required: venueMode === 'select', message: 'Venue is required' }]}
                            >
                                <Select
                                    style={{ width: '70%' }}
                                    options={KNOWN_VENUES}
                                    placeholder="Select venue"
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                name="customVenue"
                                noStyle
                                rules={[{ required: venueMode === 'custom', message: 'Custom venue is required' }]}
                            >
                                <Input style={{ width: '70%' }} placeholder="Enter custom venue" />
                            </Form.Item>
                        )}
                    </Input.Group>
                </Form.Item>

                <Form.Item
                    name="account"
                    label="Account"
                    rules={[{ required: true, message: 'Account is required' }]}
                >
                    <Input placeholder="Account identifier" />
                </Form.Item>

                <Form.Item
                    name="createdAt"
                    label="Date"
                    rules={[{ required: true, message: 'Date is required' }]}
                >
                    <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>

                {/* Sold Fields */}
                {showSoldFields && (
                    <>
                        <Form.Item
                            name="salePrice"
                            label="Sale Price"
                            rules={[{ required: true, message: 'Sale price is required' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0.01}
                                precision={2}
                                prefix="$"
                                placeholder="0.00"
                            />
                        </Form.Item>

                        <Form.Item name="transferType" label="Transfer Type">
                            <Select options={TRANSFER_TYPES} />
                        </Form.Item>

                        <Form.Item name="payoutType" label="Payout Type">
                            <Select options={PAYOUT_TYPES} onChange={handlePayoutTypeChange} />
                        </Form.Item>

                        {showInstallmentFields && (
                            <Form.Item name="installments" label="Number of Installments">
                                <InputNumber style={{ width: '100%' }} min={1} precision={0} />
                            </Form.Item>
                        )}
                    </>
                )}

                {/* Payout Fields */}
                {showPayoutFields && (
                    <>
                        <Form.Item
                            name="amount"
                            label="Amount"
                            rules={[{ required: true, message: 'Amount is required' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0.01}
                                precision={2}
                                prefix="$"
                                placeholder="0.00"
                            />
                        </Form.Item>

                        <Form.Item name="payoutType" label="Payout Type">
                            <Select options={PAYOUT_TYPES} onChange={handlePayoutTypeChange} />
                        </Form.Item>

                        <Form.Item name="currency" label="Currency">
                            <Select options={CURRENCIES} />
                        </Form.Item>

                        {showInstallmentFields && (
                            <>
                                <Form.Item
                                    name="installmentNumber"
                                    label="Installment Number"
                                    rules={[{ required: true, message: 'Installment number is required' }]}
                                >
                                    <InputNumber style={{ width: '100%' }} min={1} precision={0} />
                                </Form.Item>

                                <Form.Item
                                    name="totalInstallments"
                                    label="Total Installments"
                                    rules={[{ required: true, message: 'Total installments is required' }]}
                                >
                                    <InputNumber style={{ width: '100%' }} min={1} precision={0} />
                                </Form.Item>

                                <Form.Item name="remainingBalance" label="Remaining Balance">
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={2}
                                        prefix="$"
                                        placeholder="0.00"
                                    />
                                </Form.Item>
                            </>
                        )}
                    </>
                )}

                {/* Transfer Fields */}
                {showTransferFields && (
                    <Form.Item name="transferType" label="Transfer Type">
                        <Select options={TRANSFER_TYPES} />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default AddSalesRecordModal;
